<template>
  <div :class="controlClasses" class="mds-form-control mds-date-range-input">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <div ref="calendar-wrap" class="date-value" :class="dateValueClasses">
          <template v-if="showInputLayoutString">
            <input
              ref="date-common-input"
              :value="selectedDate"
              :placeholder="placeholder"
              @input="onPlaceholderInputChange"
              @focus="showCalendar"
            />
          </template>

          <template v-if="showInputLayoutInput">
            <VInputMask
              ref="date-from-input"
              :disabled="disabled"
              :clearable="clearable"
              :placeholder="placeholderDateFrom"
              :mask="Utils.date.dateMask"
              :value="dateFromString"
              raw
              @focus="showCalendar"
              @input="onInput($event, 'dateFrom')"
            />
            -
            <VInputMask
              ref="date-to-input"
              :disabled="disabled"
              :clearable="clearable"
              :placeholder="placeholderDateTo"
              :mask="Utils.date.dateMask"
              :value="dateToString"
              raw
              @focus="showCalendar"
              @input="onInput($event, 'dateTo')"
            />
          </template>
        </div>

        <button
          v-if="showClear"
          class="clear"
          type="button"
          tabindex="-1"
          @click.stop="onClear"
        >
          <VIcon material="cancel" />
        </button>

        <VIcon
          class="calendar-icon"
          material="calendar_today"
          @click="onClickIcon"
        />
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>

      <VPopup
        class="mds-date-range-input-popup"
        :offset="4"
        :target="$refs['calendar-wrap']"
        :visible="calendarVisible"
        @update:visible="this.toggleCalendar"
        no-pointer
        @click.stop
      >
        <div class="mds-date-range-input__calendar">
          <div
            v-if="showCalendarControls"
            class="mds-date-range-input__calendar__form"
          >
            <div class="presets">
              <button
                :class="{ active: isPresetActive(preset) }"
                :key="preset.id"
                @click="onPresetClick(preset)"
                type="button"
                tabindex="-1"
                v-for="preset of presets"
              >
                {{ preset.label }}
              </button>
            </div>

            <div class="inputs">
              <div class="inputs-item">
                <label>Дата от</label>
                <VInputMask
                  ref="popup-date-from-input"
                  :disabled="disabled"
                  :clearable="clearable"
                  :placeholder="placeholderDateFrom"
                  :mask="Utils.date.dateMask"
                  :value="dateFromString"
                  raw
                  @input="onInput($event, 'dateFrom')"
                />
              </div>

              <div class="inputs-item">
                <label>Дата до</label>
                <VInputMask
                  ref="popup-date-to-input"
                  :disabled="disabled"
                  :clearable="clearable"
                  :placeholder="placeholderDateTo"
                  :mask="Utils.date.dateMask"
                  :value="dateToString"
                  raw
                  @input="onInput($event, 'dateTo')"
                />
              </div>
            </div>

            <div class="buttons">
              <button class="mds-btn" @click="onClear">Очистить</button>
            </div>
          </div>

          <VMonthRenderer
            v-for="(m, index) of monthCount"
            :key="uid + '_' + index"
            :first-day-of-week="1"
            :is-first="index === 0"
            :is-in-range="isInRange"
            :is-disabled="isDisabled"
            :is-selected="isSelected"
            :is-last="index + 1 === showMonths"
            :month="dataForMonthRender[index].month"
            :year="dataForMonthRender[index].year"
            @change-month="onChangeMonth"
            @mouse-enter-date="onMouseEnterDate"
            @mouse-leave-date="onMouseLeaveDate"
            @select-day="onSelectDay"
            @wheel.prevent="onMonthScroll"
          />
        </div>
        <div class="popup-helper-text" v-if="hasPopupHelperTextSlot">
          <!-- @slot Слот вспомогательного текста для попап календаря -->
          <slot name="popup-helper-text" />
        </div>
      </VPopup>
    </div>
  </div>
</template>

<style lang="scss" src="./VDateRangeInput.scss" />

<script lang="ts">
import Core         from "@/core/Core";
import Utils        from "@/core/utils/Utils";
import DateSimple   from "@/core/types/DateSimple";
import VBaseControl from "@/core/services/VBaseControl";

const DATE_RANGE_INPUT_LAYOUTS = ["string", "input"] as const;

export default defineComponent({
  name: "VDateRangeInput",

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** Дата начала выбранного периода */
    dateFrom: {
      type:    [String, Date, DateSimple],
      default: null,
    },
    /** Дата окончания выбранного периода */
    dateTo: {
      type:    [String, Date, DateSimple],
      default: null,
    },
    /** Иконка */
    icon: {
      type:    String,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: null,
    },
    /** Количество месяцев в попапе */
    showMonths: {
      type:    Number,
      default: 2,
    },
    /** Выравнивание текста */
    textAlign: {
      type:    String,
      default: null,
      validate(value: string) {
        return ["left", "center", "right"].includes(value);
      },
    },
    showCalendarControls: {
      type:    Boolean,
      default: false,
    },
    inputLayout: {
      type:      String,
      default:   "string",
      validator: (v: string) => DATE_RANGE_INPUT_LAYOUTS.includes(v as any),
    },
  },

  emits: ['update:dateFrom', 'update:dateTo', 'blur'],

  data() {
    return {
      Utils,
      selectedDateFrom: null,
      selectedDateTo:   null,
      preSelectedDate:  null,
      visibleDate:      null,
      calendarVisible:  false,
      presets: [
        { label: "Сегодня",        id: "today"      },
        { label: "Вчера",          id: "yesterday"  },
        { label: "Эта неделя",     id: "thisWeek"   },
        { label: "Прошлая неделя", id: "lastWeek"   },
        { label: "Этот месяц",     id: "thisMonth"  },
        { label: "Прошлый месяц",  id: "lastMonth"  },
      ],
    };
  },

  computed: {
    placeholderDateFrom(): string {
      return this.placeholder === null ? "Дата от" : `${this.placeholder}, от`;
    },

    placeholderDateTo(): string {
      return this.placeholder === null ? "Дата до" : `${this.placeholder}, до`;
    },

    showInputLayoutString(): boolean {
      return this.inputLayout === "string";
    },

    showInputLayoutInput(): boolean {
      return this.inputLayout === "input";
    },

    monthCount() {
      return new Array(this.showMonths).fill(null);
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true)             classes.push("clearable");
      if (this.showInputLayoutString === true) classes.push("layout-string");
      if (this.showInputLayoutInput  === true) classes.push("layout-input");
      return classes;
    },

    selectedDate(): string | null {
      if (this.selectedDateFrom === null && this.selectedDateTo === null) return null;
      return Utils.date.formatRange(this.selectedDateFrom, this.selectedDateTo);
    },

    dateValueClasses(): string[] {
      const classes: string[] = [];
      if (this.calendarVisible === true) classes.push("focus");
      classes.push(`layout-${this.inputLayout}`);
      return classes;
    },

    dateFromString(): string {
      return Utils.date.format(this.selectedDateFrom);
    },

    dateToString(): string {
      return Utils.date.format(this.selectedDateTo);
    },

    showClear(): boolean {
      return this.clearable === true
        && (this.selectedDateFrom !== null || this.selectedDateTo !== null)
        && this.disabled === false
        && this.inputLayout === "string";
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    hasPopupHelperTextSlot(): boolean {
      return this.$slots["popup-helper-text"] !== undefined;
    },

    dataForMonthRender() {
      return new Array(this.showMonths).fill(null).map((item, index) => {
        let month = this.visibleDate.getMonth() + index;
        let year  = this.visibleDate.getFullYear();
        if (month > 11) {
          month = 0;
          year += 1;
        }
        return { month, year };
      });
    },

    hasValidationMethod(): boolean {
      return this.validateMethod !== null;
    },
  },

  watch: {
    dateFrom(newVal, oldVal) {
      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validation.markDirty();
      }
      this.selectedDateFrom = Utils.date.toDayStart(newVal);
    },
    dateTo(newVal, oldVal) {
      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validation.markDirty();
      }
      this.selectedDateTo = Utils.date.toDayEnd(newVal);
    },
    selectedDateFrom() {
      this.validate();
    },
    selectedDateTo() {
      this.validate();
    },
  },

  created() {
    // Если дату передали строкой — превращаем в объект.
    if (this.dateFrom instanceof Date) {
      this.selectedDateFrom = Utils.date.toDayStart(this.dateFrom);
    } else if (typeof this.dateFrom === "string" && isNaN(Date.parse(this.dateFrom)) === false) {
      this.selectedDateFrom = Utils.date.toDayStart(new Date(this.dateFrom));
      this.$emit("update:dateFrom", this.selectedDateFrom);
    }

    if (this.dateTo instanceof Date) {
      this.selectedDateTo = Utils.date.toDayEnd(this.dateTo);
    } else if (typeof this.dateTo === "string" && isNaN(Date.parse(this.dateTo)) === false) {
      this.selectedDateTo = Utils.date.toDayEnd(new Date(this.dateTo));
      this.$emit("update:dateTo", Utils.date.toDayEnd(this.selectedDateTo));
    }

    if (this.selectedDateFrom !== null) {
      this.visibleDate = this.selectedDateFrom;
    } else {
      this.visibleDate = new Date();
    }
  },

  methods: {
    onClickIcon(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.showInputLayoutString === true) {
        this.$refs["date-common-input"].focus();
      }
    },

    onChangeMonth(data) {
      const date = Utils.date.cloneDate(this.visibleDate);
      if (data.direction === "next") {
        date.setMonth(this.visibleDate.getMonth() + 1);
      } else {
        date.setMonth(this.visibleDate.getMonth() - 1);
      }
      this.visibleDate = date;
    },

    onSelectDay(date) {
      if (this.selectedDateFrom === null || (this.selectedDateFrom !== null && this.selectedDateTo !== null)) {
        this.selectedDateFrom = Utils.date.toDayStart(date);
        this.selectedDateTo   = null;
        this.$emit("update:dateFrom", this.selectedDateFrom);
        this.$emit("update:dateTo", Utils.date.toDayEnd(this.selectedDateTo));
        return;
      }

      if (date.getTime() < this.selectedDateFrom.getTime()) {
        this.selectedDateTo   = Utils.date.toDayEnd(this.selectedDateFrom);
        this.selectedDateFrom = Utils.date.toDayStart(date);
      } else {
        this.selectedDateTo = Utils.date.toDayEnd(date);
      }

      this.$emit("update:dateFrom", this.selectedDateFrom);
      this.$emit("update:dateTo", Utils.date.toDayEnd(this.selectedDateTo));

      if (this.selectedDateFrom !== null && this.selectedDateTo !== null) {
        this.hideCalendar();
      }
    },

    onMouseEnterDate(date) {
      this.preSelectedDate = date;
    },

    onMouseLeaveDate() {
      this.preSelectedDate = null;
    },

    isSelected(date) {
      if (this.selectedDateFrom !== null && this.selectedDateTo !== null) {
        const dateFrom = Utils.date.toDayStart(this.selectedDateFrom).getTime();
        const dateTo   = Utils.date.toDayStart(this.selectedDateTo).getTime();
        const dateTemp = Utils.date.toDayStart(date).getTime();
        return dateFrom <= dateTemp && dateTemp <= dateTo;
      }
      return Utils.date.format(this.selectedDateFrom) === Utils.date.format(date);
    },

    isInRange(date) {
      if (
        this.preSelectedDate === null
        || this.selectedDateFrom === null
        || (this.selectedDateFrom !== null && this.selectedDateTo !== null)
      ) {
        return false;
      }
      const dateFrom = this.selectedDateFrom.getTime();
      const dateTo   = this.preSelectedDate.getTime();
      const dateTemp = date.getTime();
      return (dateFrom < dateTemp && dateTemp < dateTo) || (dateFrom > dateTemp && dateTemp > dateTo);
    },

    isDisabled(date) {
			/** Чтобы определить является ли передаваемая дата допустимой или нет,
			 * нам нужно просто проверить вписывается ли она в рамки, не парясь
			 * с диапазонами*/
			if (this.hasValidationMethod) return this.validateMethod(date);
      return null;
    },

    onMonthScroll(event) {
			// Разные браузеры поддерживают разные свойства события wheel
      const delta = event.deltaY || event.detail || event.wheelDelta;
      const date  = Utils.date.cloneDate(this.visibleDate);
      if (delta < 0) {
        if (date.getMonth() === 0) {
          date.setFullYear(date.getFullYear() - 1);
          date.setMonth(11);
        } else {
          date.setMonth(date.getMonth() - 1);
        }
      } else {
        if (date.getMonth() === 11) {
          date.setFullYear(date.getFullYear() + 1);
          date.setMonth(0);
        } else {
          date.setMonth(date.getMonth() + 1);
        }
      }
      this.visibleDate = date;
    },

    onInput(event, field) {
      if (event.fullfilled === false) {
        if (event.masked.length === 0) {
          switch (field) {
            case "dateFrom":
              this.selectedDateFrom = null;
              this.$emit("update:dateFrom", this.selectedDateFrom);
              break;
            case "dateTo":
              this.selectedDateTo = null;
              this.$emit("update:dateTo", this.selectedDateTo);
              break;
          }
        }
        return;
      }

      const date = event.masked;
      switch (field) {
        case "dateFrom":
          if (Utils.date.validInputString(date) === true) {
            const _date = Utils.date.createFromInputString(date);
            this.selectedDateFrom = Utils.date.toDayStart(_date);
          } else {
            this.selectedDateFrom = null;
            this.selectedDateTo   = null;
          }
          this.$emit("update:dateFrom", this.selectedDateFrom);
          this.$emit("update:dateTo", Utils.date.toDayEnd(this.selectedDateTo));
          break;
        case "dateTo":
          if (Utils.date.validInputString(date) === true) {
            const _date = Utils.date.createFromInputString(date);
            this.selectedDateTo = Utils.date.toDayEnd(_date);
            if (this.selectedDateTo < this.selectedDateFrom) {
              const { selectedDateFrom, selectedDateTo } = this;
              this.selectedDateFrom = Utils.date.toDayStart(selectedDateTo);
              this.selectedDateTo   = Utils.date.toDayEnd(selectedDateFrom);
              this.$emit("update:dateFrom", this.selectedDateFrom);
            }
            this.$emit("update:dateTo", Utils.date.toDayEnd(this.selectedDateTo));
          } else {
            this.selectedDateTo = null;
          }
          break;
      }

      if (this.selectedDateFrom !== null && this.selectedDateTo !== null) {
        this.hideCalendar();
      }
    },

    onClear() {
      this.selectedDateFrom = null;
      this.selectedDateTo   = null;
      this.$emit("update:dateFrom", this.selectedDateFrom);
      this.$emit("update:dateTo", this.selectedDateTo);
      this.$emit("blur", null);
      this.toggleCalendar(false);
    },

    showCalendar() {
      if (this.disabled === true) return;
      this.validation.markTouched();
      this.toggleCalendar(true);
      window.addEventListener("keydown", this.keyDownListeners);
    },

    hideCalendar() {
      this.toggleCalendar(false);
      this.$emit("blur", null);
      window.removeEventListener("keydown", this.keyDownListeners);
    },

    toggleCalendar(value) {
      this.calendarVisible = value;
      if (value === true) {
        this.visibleDate = this.selectedDateFrom instanceof Date
          ? Utils.date.cloneDate(this.selectedDateFrom)
          : new Date();
      }
    },

    isPresetActive(preset) {
      if (this.selectedDateFrom === null || this.selectedDateTo === null) return false;

      const { dateFrom, dateTo } = this.calculatePresetDateRange(preset.id);
      return (
        Utils.date.format(this.selectedDateFrom) === Utils.date.format(dateFrom)
        && Utils.date.format(this.selectedDateTo) === Utils.date.format(dateTo)
      );
    },

    onPresetClick(preset) {
      const { dateFrom, dateTo } = this.calculatePresetDateRange(preset.id);
      this.selectedDateFrom = Utils.date.toDayStart(dateFrom);
      this.selectedDateTo   = Utils.date.toDayEnd(dateTo);
      this.$emit("update:dateFrom", this.selectedDateFrom);
      this.$emit("update:dateTo", Utils.date.toDayEnd(this.selectedDateTo));
      this.hideCalendar();
    },

    calculatePresetDateRange(id) {
      let period = new Date();
      let dateFrom = null;
      let dateTo   = null;

      switch (id) {
        case "today":
          dateFrom = period;
          dateTo = period;
          break;
        case "yesterday":
          period.setDate(period.getDate() - 1);
          dateFrom = period;
          dateTo = period;
          break;
        case "thisWeek":
          period = Utils.date.getCurrentWeekStartDate();
          dateFrom = Utils.date.cloneDate(period);
          period.setDate(period.getDate() + 6);
          dateTo = period;
          break;
        case "lastWeek":
          period = Utils.date.getCurrentWeekStartDate();
          period.setDate(period.getDate() - 7);
          dateFrom = Utils.date.cloneDate(period);
          period.setDate(period.getDate() + 6);
          dateTo = period;
          break;
        case "thisMonth":
          period.setDate(1);
          dateFrom = Utils.date.cloneDate(period);
          period.setMonth(period.getMonth() + 1);
          period.setDate(period.getDate() - 1);
          dateTo = period;
          break;
        case "lastMonth":
          period.setDate(1);
          period.setMonth(period.getMonth() - 1);
          dateFrom = Utils.date.cloneDate(period);
          period.setMonth(period.getMonth() + 1);
          period.setDate(period.getDate() - 1);
          dateTo = period;
          break;
      }
      return { dateFrom, dateTo };
    },

		/** на основание кастомного метода валидации проверяем даты */
    isDateDisabled(date) {
      return this.hasValidationMethod === true
        ? this.validateMethod(date) !== null
        : false;
    },

		/** проверяем находится ли невалидная дата в выбранном периоде */
    isRangeContainsDisabledDate(dateFrom, dateTo) {
      if (this.hasValidationMethod === false) return false;

      let currentDate = new Date(dateFrom);
      while (currentDate <= dateTo) {
        if (this.isDateDisabled(currentDate) === true) return true;
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return false;
    },

    /**
     * Запускает валидацию текущего диапазона через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     *
     * Поверх стандартных правил применяем доп. бизнес-проверку: выбранный
     * период не должен содержать дат, отмеченных validateMethod как недоступные.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;

      await this.validation.validate({
        dateFrom: this.selectedDateFrom,
        dateTo:   this.selectedDateTo,
      });

      this.validateRangeDoesNotContainDisabledDates();
    },

    /**
     * Доп. правило: диапазон не должен содержать недоступных дат.
     *
     * "Недоступные" определяются validateMethod — если у контрола его нет,
     * проверять нечего. Если хоть одна дата внутри диапазона недоступна —
     * пушим ошибку и сбрасываем isValid.
     */
    validateRangeDoesNotContainDisabledDates(): void {
      if (this.hasValidationMethod === false) return;

      const containsDisabled = this.isRangeContainsDisabledDate(
        this.selectedDateFrom,
        this.selectedDateTo,
      );

      if (containsDisabled === true) {
        this.validation.errors.push("Выбранный период содержит недоступные даты");
        this.validation.isValid = false;
      }
    },

    onPlaceholderInputChange(event) {
      const input = event.target;
      input.value = this.selectedDate;
    },

    keyDownListeners(event) {
      switch (event.key) {
        case "Tab": {
          const popupDateFromInput = this.$refs["popup-date-from-input"]
          && this.$refs["popup-date-from-input"].hasOwnProperty("$el")
            ? this.$refs["popup-date-from-input"].$el.querySelector("input")
            : null;
          const popupDateToInput = this.$refs["popup-date-to-input"]
          && this.$refs["popup-date-to-input"].hasOwnProperty("$el")
            ? this.$refs["popup-date-to-input"].$el.querySelector("input")
            : null;
          const input = document.activeElement;

          if (
            (popupDateFromInput !== null && input === popupDateFromInput)
            || (popupDateToInput !== null && input === popupDateToInput)
          ) {
            return;
          }

          if (this.showInputLayoutString === true) {
            this.hideCalendar();
          } else if (this.showInputLayoutInput === true) {
            const dateToInput = this.$refs["date-to-input"]
            && this.$refs["date-to-input"].hasOwnProperty("$el")
              ? this.$refs["date-to-input"].$el.querySelector("input")
              : null;
            if (dateToInput !== null && input === dateToInput) {
              this.hideCalendar();
            }
          }
          break;
        }
        case "Escape": {
          const input = document.activeElement;
          if (input && typeof input.blur === "function") input.blur();
          this.hideCalendar();
          break;
        }
      }
    },
  },
});
</script>
